// Deploy-role suggestions offered on the Deployment Accounts form.
//
// WHY THIS FILE EXISTS
//
// A managed STATIC_SITE deploy stage assumes a role in the target account and
// runs `aws s3 sync` into that account's `*-ui` bucket. Which role it assumes is
// configuration, not something the platform can infer — so the operator has to
// enter an ARN per account, per pipeline. Four pipelines times three accounts is
// twelve ARNs, each containing its own account id twice. Typing those by hand is
// how a wrong-but-plausible ARN gets in.
//
// These are SUGGESTIONS, not a whitelist. The field is an Autocomplete: pick the
// offered value, or type any other ARN. Nothing here constrains what can be
// saved.
//
// ⚠️ WHY THE LEGACY ROLE IS STILL THE DEFAULT, 2026-08-29
//
// The obvious candidate was the CDK bootstrap role, `cdk-hnb659fds-deploy-role-
// <account>-<region>`, which exists in every account and needs no maintenance.
// It does not work for this, and the reason is not obvious from reading its
// action list:
//
//   "Action":   ["s3:GetObject*","s3:GetBucket*","s3:List*","s3:Abort*",
//                "s3:DeleteObject*","s3:PutObject*"],
//   "Resource": "*",
//   "Condition": {"StringNotEquals": {"s3:ResourceAccount": "<own account>"}}
//
// That condition means the grant applies only to buckets NOT owned by the
// account — it is there so the role can read the tools-account artifact bucket.
// A UI bucket sits in the same account, so it is excluded. Pointing a deploy
// stage at the CDK role produces:
//
//   AccessDenied ... not authorized to perform: s3:ListBucket on resource:
//   "arn:aws:s3:::flexinet-marketplace-app-infra-298878876875-ui"
//
// Measured on execution fc3df7bc, 2026-08-29. The AssumeRole itself succeeds —
// this fails one step later, which is what makes it easy to misdiagnose.
//
// `CodePipelineCrossAccountRole` carries an unconditional
// `s3:Get*/Put*/ListBucket` on `*` and is what the legacy pipelines have always
// used. It is broader than a static-site deploy needs, which is a good reason to
// replace it eventually — but replacing it means creating a role that can
// actually do the job, and that is its own piece of work (FNP-1267).
//
// ⚠️ THE ROLE IS ABSENT FROM EVERY DEV ACCOUNT. It was only ever created where a
// pipeline needed it, and the legacy pipelines had no dev stage. Until it exists
// in dev, a dev deploy stage cannot work whichever ARN is chosen here.

export type DeploymentStage = 'dev' | 'test' | 'prod';

// The role every legacy pipeline assumes. Not defined in any repo — created
// out-of-band, like the CDK bootstrap roles.
const LEGACY_DEPLOY_ROLE_NAME = 'CodePipelineCrossAccountRole';

// The CDK bootstrap deploy role. Offered as a second suggestion because it is
// the right answer for a CLOUDFORMATION deploy stage, where the same-account S3
// condition above is irrelevant. Do not choose it for an S3 stage.
const CDK_BOOTSTRAP_DEPLOY_ROLE = (accountId: string, region: string) =>
    `cdk-hnb659fds-deploy-role-${accountId}-${region}`;

export const DEFAULT_DEPLOY_REGION = 'us-west-2';

/**
 * The environment an account belongs to, from its name.
 *
 * Mirrors how the API pairs deploy stages to accounts — by the `-dev`/`-test`/
 * `-prod` suffix on the account name. Returns undefined rather than guessing
 * when the name does not classify, so a caller can tell "prod" from "unknown".
 */
export const inferStageFromAccountName = (
    accountName?: string,
): DeploymentStage | undefined => {
    if (!accountName) return undefined;
    const name = accountName.toLowerCase();
    if (name.endsWith('-dev')) return 'dev';
    if (name.endsWith('-test')) return 'test';
    if (name.endsWith('-prod')) return 'prod';
    return undefined;
};

export const legacyDeployRoleArn = (accountId: string): string =>
    `arn:aws:iam::${accountId}:role/${LEGACY_DEPLOY_ROLE_NAME}`;

export const cdkBootstrapDeployRoleArn = (
    accountId: string,
    region: string = DEFAULT_DEPLOY_REGION,
): string =>
    `arn:aws:iam::${accountId}:role/${CDK_BOOTSTRAP_DEPLOY_ROLE(accountId, region)}`;

/**
 * Suggested deploy-role ARNs for one deployment account, most-likely first.
 *
 * The legacy role leads because it is the only one that can currently write a
 * UI bucket. The CDK role follows for CloudFormation stages. Both are built from
 * the account's own id, so a suggestion is always self-consistent — which is the
 * actual value here, given an ARN repeats its account id twice.
 *
 * ⚠️ Returns plain ARN strings, deliberately. These become an Autocomplete's
 * options, and Mantine displays the matching option's LABEL in the input — so a
 * decorated label would show prose where a saved pipeline's ARN belongs, and
 * re-saving would persist that prose as the deploy role. Any explanation belongs
 * in the field's description, not here.
 */
export const deployRoleSuggestions = (
    accountId?: string,
    _accountName?: string,
    region: string = DEFAULT_DEPLOY_REGION,
): string[] => {
    if (!accountId) return [];

    return [
        legacyDeployRoleArn(accountId),
        cdkBootstrapDeployRoleArn(accountId, region),
    ];
};
